import React, { useState, useEffect } from 'react';
import { Button } from './button';
import { Card, CardContent } from './card';
import { X, Settings, Shield, BarChart3, Target, Cookie } from 'lucide-react';
import { useTranslation } from '../../contexts/TranslationContext';
import { CookieManager } from '../../utils/cookieManager';

const CookieConsent = () => {
  const { t } = useTranslation();
  const [showConsent, setShowConsent] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
    functional: false
  });

  // Check if user has already made a choice
  useEffect(() => {
    if (!CookieManager.hasConsent()) {
      // Show consent after a small delay for better UX
      setTimeout(() => setShowConsent(true), 1000);
    } else {
      // Load saved preferences
      setPreferences(CookieManager.getPreferences());
      // Initialize services based on saved preferences
      CookieManager.initializeAllServices();
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    
    setPreferences(allAccepted);
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    setShowConsent(false);
    
    // Initialize analytics and other services
    initializeServices(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    
    setPreferences(onlyNecessary);
    localStorage.setItem('cookieConsent', 'rejected');
    localStorage.setItem('cookiePreferences', JSON.stringify(onlyNecessary));
    setShowConsent(false);
    
    // Only initialize necessary services
    initializeServices(onlyNecessary);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', 'customized');
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    setShowConsent(false);
    setShowPreferences(false);
    
    // Initialize services based on preferences
    initializeServices(preferences);
  };

  const handlePreferenceChange = (type) => {
    if (type === 'necessary') return; // Can't disable necessary cookies
    
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const initializeServices = (prefs) => {
    // Use the CookieManager utility to initialize services
    CookieManager.initializeAllServices();
  };

  // Don't render if consent has been given
  if (!showConsent) return null;

  return (
    <>
      {/* Main Cookie Consent Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-background/95 backdrop-blur-sm border-t border-border shadow-lg">
        <div className="max-w-7xl mx-auto">
          <Card className="border-border/50 shadow-xl">
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
                {/* Cookie Icon */}
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Cookie className="w-6 h-6 text-primary" />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {t('cookies.title', 'We use cookies to enhance your experience')}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t('cookies.description', 'We use cookies to improve your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.')}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPreferences(true)}
                    className="flex items-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    {t('cookies.preferences', 'Preferences')}
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRejectAll}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {t('cookies.reject', 'Reject All')}
                  </Button>
                  
                  <Button
                    size="sm"
                    onClick={handleAcceptAll}
                    className="bg-primary hover:bg-primary/90"
                  >
                    {t('cookies.accept', 'Accept All')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Cookie Preferences Modal */}
      {showPreferences && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <Settings className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-xl font-semibold text-foreground">
                    {t('cookies.preferencesTitle', 'Cookie Preferences')}
                  </h2>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPreferences(false)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Cookie Categories */}
              <div className="space-y-6">
                {/* Necessary Cookies */}
                <div className="border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-green-500" />
                      <div>
                        <h3 className="font-medium text-foreground">
                          {t('cookies.necessary.title', 'Necessary Cookies')}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {t('cookies.necessary.description', 'Essential for the website to function properly')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-green-600 font-medium">
                        {t('cookies.alwaysActive', 'Always Active')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <BarChart3 className="w-5 h-5 text-blue-500" />
                      <div>
                        <h3 className="font-medium text-foreground">
                          {t('cookies.analytics.title', 'Analytics Cookies')}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {t('cookies.analytics.description', 'Help us understand how visitors interact with our website')}
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={() => handlePreferenceChange('analytics')}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Target className="w-5 h-5 text-purple-500" />
                      <div>
                        <h3 className="font-medium text-foreground">
                          {t('cookies.marketing.title', 'Marketing Cookies')}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {t('cookies.marketing.description', 'Used to deliver personalized advertisements and track campaign performance')}
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={() => handlePreferenceChange('marketing')}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                </div>

                {/* Functional Cookies */}
                <div className="border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Settings className="w-5 h-5 text-orange-500" />
                      <div>
                        <h3 className="font-medium text-foreground">
                          {t('cookies.functional.title', 'Functional Cookies')}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {t('cookies.functional.description', 'Enable enhanced functionality and personalization features')}
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.functional}
                        onChange={() => handlePreferenceChange('functional')}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t border-border">
                <Button
                  variant="outline"
                  onClick={() => setShowPreferences(false)}
                  className="flex-1"
                >
                  {t('cookies.cancel', 'Cancel')}
                </Button>
                <Button
                  onClick={handleSavePreferences}
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  {t('cookies.savePreferences', 'Save Preferences')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
